// Sổ chi tiết công nợ 1 nhà cung cấp — chị Thủy (kế toán) cần mở ra là thấy
// "nhập hàng ngày nào, chi trả tiền ngày nào" trên cùng một dòng thời gian.
// Mã phiếu nhập bấm được để sang thẳng phiếu.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { X, FileDown } from "lucide-react";
import { apiGet } from "@/lib/api/client";
import { formatDateTime } from "@/lib/format/date";
import { Button } from "@/components/ui/button";

interface LedgerEntry {
  kind: "import" | "payment";
  occurredAt: string;
  refId: string;
  code: string;
  debit: string;
  credit: string;
  method: string;
  note: string;
  byEmail: string;
  debtBooked: boolean;
}

interface LedgerResponse {
  supplier: { id: string; name: string; phone: string; debt: string };
  entries: LedgerEntry[];
  totalImports: string;
  totalPaid: string;
}

const METHOD_LABEL: Record<string, string> = {
  cash: "Tiền mặt",
  bank_manual: "Chuyển khoản",
};

function fmtVND(n: string | number) {
  const v = typeof n === "string" ? parseFloat(n) || 0 : n;
  return new Intl.NumberFormat("vi-VN").format(Math.round(v)) + " ₫";
}

// Xuất CSV mở được bằng Excel (BOM UTF-8 + CRLF) — FE chưa có lib xlsx.
function exportCsv(data: LedgerResponse) {
  const head = ["Thời gian", "Loại", "Chứng từ", "Nợ tăng", "Đã trả", "Phương thức", "Người thực hiện", "Ghi chú"];
  const lines = data.entries.map((e) => [
    formatDateTime(e.occurredAt),
    e.kind === "import" ? "Nhập hàng" : "Chi trả",
    e.code || "—",
    e.kind === "import" ? Math.round(parseFloat(e.debit) || 0) : "",
    e.kind === "payment" ? Math.round(parseFloat(e.credit) || 0) : "",
    e.method ? METHOD_LABEL[e.method] ?? e.method : "",
    e.byEmail || "",
    (e.note || "").replace(/[\r\n]+/g, " "),
  ]);
  const csv = [head, ...lines]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cong-no-NCC-${data.supplier.name.replace(/[^\w]+/g, "-")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function SupplierLedgerModal({
  supplierId,
  onClose,
}: {
  supplierId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["supplier-ledger", supplierId],
    queryFn: () => apiGet<LedgerResponse>(`/v1/suppliers/${supplierId}/ledger`),
  });

  const hasUnbooked = (data?.entries ?? []).some((e) => e.kind === "import" && !e.debtBooked);

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-background">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold">
            Công nợ NCC — {data?.supplier.name ?? "…"}
          </h2>
          {data?.supplier.phone && (
            <p className="text-xs text-muted-foreground">{data.supplier.phone}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {data && data.entries.length > 0 && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => exportCsv(data)}>
              <FileDown size={15} /> Xuất Excel
            </Button>
          )}
          <button type="button" onClick={onClose} aria-label="Đóng" className="rounded p-1 hover:bg-muted">
            <X size={20} />
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-1 gap-2 border-b bg-muted/20 px-4 py-3 sm:grid-cols-3">
          <div className="rounded bg-background p-2">
            <p className="text-xs text-muted-foreground">Tổng tiền hàng đã nhập</p>
            <p className="font-semibold tabular-nums">{fmtVND(data.totalImports)}</p>
          </div>
          <div className="rounded bg-background p-2">
            <p className="text-xs text-muted-foreground">Tổng đã trả</p>
            <p className="font-semibold tabular-nums text-green-600">{fmtVND(data.totalPaid)}</p>
          </div>
          <div className="rounded bg-background p-2">
            <p className="text-xs text-muted-foreground">Công nợ hiện tại</p>
            <p className="font-semibold tabular-nums text-amber-600 dark:text-amber-400">
              {fmtVND(data.supplier.debt)}
            </p>
          </div>
        </div>
      )}

      {hasUnbooked && (
        <p className="border-b bg-amber-50 px-4 py-2 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
          Dòng có dấu ✻ là phiếu xác nhận trước khi hệ thống có sổ chi — tiền hàng của
          những phiếu đó KHÔNG tự cộng vào công nợ (đã nằm trong số dư đầu kỳ nhập tay).
          Vì vậy "Tổng tiền hàng − Tổng đã trả" có thể không khớp "Công nợ hiện tại".
        </p>
      )}

      <div className="flex-1 overflow-auto px-4 py-3">
        {isLoading ? (
          <p className="py-8 text-center text-muted-foreground">Đang tải…</p>
        ) : (data?.entries.length ?? 0) === 0 ? (
          <p className="py-8 text-center text-muted-foreground">
            NCC này chưa có phiếu nhập nào đã xác nhận và chưa có khoản chi nào.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2 text-left">Thời gian</th>
                <th className="py-2 text-left">Loại</th>
                <th className="py-2 text-left">Chứng từ</th>
                <th className="py-2 text-right">Nợ tăng</th>
                <th className="py-2 text-right">Đã trả</th>
                <th className="py-2 pl-3 text-left">Phương thức</th>
                <th className="py-2 text-left">Người thực hiện</th>
                <th className="py-2 text-left">Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              {data?.entries.map((e) => (
                <tr key={`${e.kind}-${e.refId}`} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-1.5 whitespace-nowrap">{formatDateTime(e.occurredAt)}</td>
                  <td className="py-1.5">
                    {e.kind === "import" ? (
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                        Nhập hàng
                      </span>
                    ) : (
                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700 dark:bg-green-950/50 dark:text-green-300">
                        Chi trả
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 font-mono text-xs">
                    {e.kind === "import" && e.code ? (
                      <Link
                        to="/inventory/imports/$id"
                        params={{ id: e.refId }}
                        onClick={onClose}
                        className="text-primary hover:underline"
                      >
                        {e.code}
                      </Link>
                    ) : (
                      e.code || <span className="text-muted-foreground">Trả nợ chung</span>
                    )}
                    {e.kind === "import" && !e.debtBooked && (
                      <span title="Phiếu cũ — tiền hàng không tự cộng vào công nợ"> ✻</span>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {e.kind === "import" ? fmtVND(e.debit) : "—"}
                  </td>
                  <td className="py-1.5 text-right font-medium tabular-nums text-green-600">
                    {e.kind === "payment" ? fmtVND(e.credit) : "—"}
                  </td>
                  <td className="py-1.5 pl-3">{e.method ? METHOD_LABEL[e.method] ?? e.method : "—"}</td>
                  <td className="py-1.5 text-xs text-muted-foreground">{e.byEmail || "—"}</td>
                  <td className="py-1.5 text-xs">{e.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
